/**
 * The two reads the Reports page makes (POS2-1137 CP1).
 *
 * Both are site-scoped and both are guarded by `waste:read` on the backend — the Dashboard's own
 * permission — so a head chef on the seeded Staff role can open this page (decision 32).
 *
 * **Neither query asks for delivery telemetry, and there is nothing to ask for.** Open rates and
 * read rates never appear in the customer UI; `GET /reports/runs` returns the run, not its
 * deliveries. That is an acceptance criterion, not a preference.
 *
 * A failure leaves the page rendering four never-configured cards rather than an error screen: the
 * catalogue is static and a card with no schedule row is a real state, so an unreachable endpoint
 * degrades to the state a brand-new site is in anyway.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useAdminOrgStore } from "@/stores/admin-org";
import { apiError, type ApiRequestError } from "@/lib/api-error";
import { useTranslation } from "@/i18n/use-translation";
import type { Locale } from "@/i18n/config";
import { formatDateTime } from "@/lib/format";
import type { ReportKey } from "@/lib/report-catalogue";
// The leaf, not the barrel: this hook is reached from the eager Reports page (`report-chunk.test
// .ts`). Type-only either way, so it costs nothing today — but the next edit to this line is more
// likely to be right if the file it points at is the one it needs.
import type { ReportPayload } from "@/reports/document/types";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export type Cadence = "weekdays" | "every_day" | "weekly" | "first_of_month" | "last_friday";

export interface ReportRecipient {
  id: string;
  email: string;
  user_id: string | null;
  /** Set when the recipient has been taken off; the card counts only the live ones. */
  removed_at: string | null;
  /** Why. "Everyone unsubscribed" and "somebody took them off" are different sentences. */
  removed_reason: "user" | "unsubscribed" | "left_team" | null;
}

/** Who last touched a schedule. A null actor is a system write — an auto-pause, a one-click
 * unsubscribe from a mail client — and stays null rather than becoming "unknown". */
export interface ScheduleChange {
  actor_name: string | null;
  action: string;
  at: string;
}

export interface ReportSchedule {
  id: string;
  report_type: ReportKey;
  cadence: Cadence;
  /** Weekly only. 0 = Monday, matching `zoned-day.ts` and the report week everywhere. */
  send_dow: number | null;
  /** Wire JSON, `"07:00:00"`. */
  send_time_local: string;
  enabled_sections: string[];
  is_paused: boolean;
  pause_reason: "user" | "no_recipients" | null;
  next_run_at: string | null;
  updated_at: string;
  report_recipients: ReportRecipient[];
  /** Live recipients whose most recent word from the provider was a *hard* bounce. Computed by
   * the API from `report_deliveries`: a browser deriving it would be reading a table it has no
   * business knowing about, and a soft bounce is a full mailbox rather than a wrong address. */
  bounced_emails: string[];
  last_change: ScheduleChange | null;
  /** Whether the period a send **today** would cover has already gone out (POS2-1257). A server
   * answer: `resolve_period` owns what a send on a given day covers, and a Weekly covers the last
   * complete week whichever day it is sent on — so re-daying one after that week's send changes
   * nothing about today either. The editor warns on this before a save rather than after. */
  current_period_sent: boolean;
  /** Only on a save's response: somebody else moved this row between the editor loading it and
   * this save landing. Never a refusal — the write has already happened (decision 13). */
  concurrent_change?: ScheduleChange | null;
}

export interface ReportRun {
  id: string;
  report_type: ReportKey;
  period_start: string;
  period_end: string;
  period_label: string;
  status: string;
  page_count: number | null;
  /** `scheduled` — the tick sent it; `manual` — somebody pressed "Send to all recipients now"
   * (POS2-1316). Without the distinction a manual send and the scheduled one for the same period
   * read as a duplicate-send bug. */
  origin: "scheduled" | "manual";
  /** When this report started reaching people — the earliest of its deliveries. `null` while it is
   * still waiting to be sent with the others its recipients are owed (POS2-1312). */
  sent_at: string | null;
  generated_at: string | null;
  created_at: string;
  /** Who this send actually reached, as addressed — from `report_deliveries`, never from the
   * schedule's current list, which would rewrite history every time somebody edits it. */
  recipients: string[];
}

function useReportQuery<T>(path: string, siteId: string | null, key: string) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const orgId = useAdminOrgStore((s) => s.orgId);

  return useQuery<T[]>({
    queryKey: ["reports", key, orgId, siteId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/reports${path}?site_id=${siteId}`, { headers });
      if (!res.ok) throw await apiError(t, res);
      return ((await res.json()) as { data: T[] }).data;
    },
    enabled: !!siteId && !!headers.Authorization.replace("Bearer ", ""),
    staleTime: 30_000,
  });
}

/** This site's configured reports — nought to four rows. Absence is the never-configured state. */
export function useReportSchedules(siteId: string | null) {
  const query = useReportQuery<ReportSchedule>("", siteId, "schedules");
  return { ...query, data: query.data?.map(withCardState) };
}

/** What has actually been sent, newest first. Test sends and failed generations are both absent. */
export function useReportRuns(siteId: string | null) {
  const query = useReportQuery<ReportRun>("/runs", siteId, "runs");
  return {
    ...query,
    data: query.data?.map((run) => ({ ...run, recipients: run.recipients ?? [] })),
  };
}

/**
 * The card fields, defaulted at the boundary — and this is a boundary, not defensive code inside
 * the app.
 *
 * `bounced_emails`, `last_change` and a run's `recipients` were added by CP4, and the frontend and
 * the backend do not deploy together: the app goes to Cloudflare Pages on merge while the API goes
 * to Railway behind a CI gate that has been known to hold. So there is a real window in which a new
 * page meets an older API, and `schedule.bounced_emails.length` on an absent field takes the whole
 * Reports screen down rather than degrading to "no bounce to report" — which is the truth anyway
 * when the server cannot say.
 */
function withCardState(schedule: ReportSchedule): ReportSchedule {
  return {
    ...schedule,
    bounced_emails: schedule.bounced_emails ?? [],
    last_change: schedule.last_change ?? null,
  };
}

/**
 * The payload one report would render from, for the period it would cover if sent today at the
 * cadence and hour the editor holds.
 *
 * Returned whole, every section it can contain, and cached per (site, report, cadence, hour) —
 * decision 9's "live section toggling never refetches" starts here: CP3 toggles a `useState` set
 * against this one response, so a tick costs nothing.
 *
 * **The draft drives the fetch, and nothing is fetched until there is one.** The header's "Arrives
 * weekly at 15:00" is written from `payload.arrival`, so the paper has to be assembled from the
 * cadence and hour the editor shows or the two disagree — which is how every report previewed at
 * 07:00 whatever its schedule said (POS2-1282). Fetching before the draft is seeded would fetch the
 * wrong report and then the right one, and this is the expensive query. While a change is answered
 * the previous paper stays up, so the editor beside it is never unmounted under the hand using it.
 *
 * **A 409 is an answer, not a failure.** A period beginning before the site went live has no
 * report to render, and the endpoint says so rather than serving a page of zeros that reads like a
 * quiet kitchen. `retry: false` so the page reaches that state in one hop instead of three.
 */
export function useReportPreview(
  siteId: string | null,
  reportType: ReportKey,
  arrival: { cadence: Cadence; send_time_local: string } | null,
) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const orgId = useAdminOrgStore((s) => s.orgId);

  return useQuery<ReportPayload, ApiRequestError>({
    queryKey: [
      "reports",
      "preview",
      orgId,
      siteId,
      reportType,
      arrival?.cadence,
      arrival?.send_time_local,
    ],
    queryFn: async () => {
      // `enabled` holds this until the editor has a draft; a fetch without one is a bug, not a state.
      if (!arrival) throw new Error("report preview fetched before the editor had a draft");
      const res = await fetch(
        `${API_URL}/api/v1/reports/preview?site_id=${siteId}&type=${reportType}` +
          `&cadence=${arrival.cadence}&send_time_local=${arrival.send_time_local}`,
        { headers },
      );
      if (!res.ok) throw await apiError(t, res);
      return ((await res.json()) as { data: ReportPayload }).data;
    },
    enabled: !!siteId && !!arrival && !!headers.Authorization.replace("Bearer ", ""),
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 60_000,
  });
}

export interface TeamMember {
  user_id: string;
  email: string;
  full_name: string | null;
  role_name: string;
  /** An empty site assignment set means org-wide access, never "no sites" (decision 6). */
  org_wide: boolean;
  /** Deactivated members are listed but never offered: the picker needs them to recognise a
   *  typed-in address and warn that the mailbox is probably closed (POS2-1304). */
  is_active: boolean;
}

/**
 * Who the recipient picker can offer, for one site (decision 5).
 *
 * Deliberately not `GET /members`: that is guarded by `user:read`, which the seeded Staff role does
 * not have while it does have `waste:read`. A head chef on Staff has to be able to open this
 * picker, so it goes through the Dashboard's own permission.
 */
export function useReportTeam(siteId: string | null) {
  return useReportQuery<TeamMember>("/team", siteId, "team");
}

/** What actually happened to a run, as far as the customer is concerned (POS2-1324).
 *
 * `sent_at` is the earliest of a run's deliveries, so it is the only field that means an email left
 * — `status` is the run's own state and a generated run has no deliveries yet. Reading either one
 * alone is how the card came to print a green tick over a generation time.
 *
 * `error` only chooses the wording once we already know nothing was sent; it never overrides a
 * `sent_at`, because a run that reached somebody and then failed a later delivery did send.
 */
export type RunOutcome = "sent" | "failed" | "waiting";

export function runOutcome(run: ReportRun): RunOutcome {
  if (run.sent_at) return "sent";
  return run.status === "error" ? "failed" : "waiting";
}

/** When a run went out, in the site's zone rather than the viewer's.
 *
 * A pure helper rather than an inline `formatDateTime` because `i18next/no-literal-string` runs in
 * `jsx-only` mode and reads `dateStyle: "medium"` inside JSX as user-facing copy — the same reason
 * `transaction-card.tsx:515` keeps its own formatter below the component.
 *
 * `generated_at` is when the PDF was made; `created_at` is the fallback for a run that has a row
 * and no file yet, which is a state the scheduler passes through.
 *
 * POS2-1324: the fallback stays, but it is no longer silent. Generation and the email are up to
 * half an hour apart since a scheduled report waits to go out with the others its recipients are
 * owed (POS2-1312), and a send that never happens leaves `sent_at` NULL for ever. Callers pair
 * this with `runOutcome` so the time is labelled by what it actually is; on its own it answers
 * "when did something last happen to this run", which is not the same question.
 */
export function sentAt(locale: Locale, run: ReportRun, timezone: string): string {
  return formatDateTime(locale, run.sent_at ?? run.generated_at ?? run.created_at, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timezone,
  });
}

/** The live recipients of a schedule — a removal is recorded, not deleted. */
export function liveRecipients(schedule: ReportSchedule): ReportRecipient[] {
  return schedule.report_recipients.filter((r) => !r.removed_at);
}
